import React, { useEffect, useRef, useState } from "react";
import { database, auth } from "../../firebaseConfig";
import { ref, get, set } from "firebase/database";
import { onAuthStateChanged, signOut } from "firebase/auth";
import axios from "axios";
import "./AdminPanel.css";

// Import admin components
import ProductsPanel from "../../components/admin/ProductsPanel";
import TopBar from "../../components/admin/TopBar";
import Sidebar from "../../components/admin/Sidebar";
import CardConfigEditor from "../../components/admin/CardConfigEditor";
import HomeConfigEditor from "../../components/admin/HomeConfigEditor";
import AboutUsConfigEditor from "../../components/admin/AboutUsConfigEditor";
import FloatingIconsConfigEditor from "../../components/admin/FloatingIconsConfigEditor";
import AdminLoginPage from "../../components/admin/AdminLoginPage";
import CategoriesManager from "../../components/admin/CategoriesManager";
import NavbarConfigEditor from "../../components/admin/NavbarConfigEditor";
import FooterConfigEditor from "../../components/admin/FooterConfigEditor";
import ProductDescriptionConfigEditor from "../../components/admin/ProductDescriptionConfigEditor";
import { config } from '../../config/config'
import HeadMetaConfigEditor from "../../components/admin/HeadMetaConfigEditor"
const API_URL = config.baseUrl;

const AdminPanel = () => {
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState("");
    const [newCategoryImage, setNewCategoryImage] = useState(null);
    // New SEO states for new category
    const [newMetaTitle, setNewMetaTitle] = useState("");
    const [newMetaDescription, setNewMetaDescription] = useState("");
    const [newMetaKeywords, setNewMetaKeywords] = useState("");

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [activeSection, setActiveSection] = useState("categories");
    const [showSidebar, setShowSidebar] = useState(true);
    const [editingImageIndex, setEditingImageIndex] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [cardConfig, setCardConfig] = useState({});

    // Refs for file inputs
    const editFileInputRef = useRef(null);
    const imageEditInputRef = useRef(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setIsAuthenticated(!!user);
            if (user) {
                fetchCategories();
                fetchCardConfig();
            }
        });
        return () => unsubscribe();
    }, []);

    const fetchCategories = async () => {
        try {
            const snapshot = await get(ref(database, "categories"));
            if (snapshot.exists()) {
                const data = snapshot.val();
                setCategories(Array.isArray(data) ? data : []);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    const fetchCardConfig = async () => {
        try {
            const snapshot = await get(ref(database, "cardConfig"));
            if (snapshot.exists()) {
                setCardConfig(snapshot.val());
            } else {
                setCardConfig({});
            }
        } catch (error) {
            console.error("Error fetching card config:", error);
        }
    };

    const handleLogout = async () => {
        await signOut(auth);
        setIsAuthenticated(false);
    };

    // File Upload Helper
    const uploadCategoryImage = async (file) => {
        if (!file) return null;
        const formData = new FormData();
        formData.append("media", file);
        try {
            const response = await axios.post(`${API_URL}upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response.data.fileUrl || null;
        } catch (error) {
            console.error("Error uploading category image:", error);
            return null;
        }
    };

    // Add Category (including SEO data)
    const addCategory = async () => {
        if (!newCategory.trim()) {
            return alert("Enter a category name");
        }
        let imageUrl = null;
        if (newCategoryImage) {
            imageUrl = await uploadCategoryImage(newCategoryImage);
        }
        const updatedCategories = [
            ...categories,
            {
                category: newCategory,
                categoryUrl: imageUrl || null,
                products: [],
                seo: {
                    metaTitle: newMetaTitle,
                    metaDescription: newMetaDescription,
                    metaKeywords: newMetaKeywords,
                },
            },
        ];
        await set(ref(database, "categories"), updatedCategories);
        // Reset new category fields
        setNewCategory("");
        setNewCategoryImage(null);
        setNewMetaTitle("");
        setNewMetaDescription("");
        setNewMetaKeywords("");
        fetchCategories();
    };

    // Edit Category – now includes SEO fields
    const editCategory = async (index, updatedData) => {
        if (!updatedData || !updatedData.title) {
            alert("Category title is required.");
            return;
        }
        const updatedCategories = [...categories];
        updatedCategories[index] = {
            ...updatedCategories[index],
            category: updatedData.title,
            description:
                updatedData.description !== undefined
                    ? updatedData.description
                    : updatedCategories[index].description,
            price:
                updatedData.price !== undefined
                    ? updatedData.price
                    : updatedCategories[index].price,
            discount:
                updatedData.discount !== undefined
                    ? updatedData.discount
                    : updatedCategories[index].discount,
            rating:
                updatedData.rating !== undefined
                    ? updatedData.rating
                    : updatedCategories[index].rating,
            seo: {
                metaTitle:
                    updatedData.metaTitle ||
                    (updatedCategories[index].seo && updatedCategories[index].seo.metaTitle) ||
                    "",
                metaDescription:
                    updatedData.metaDescription ||
                    (updatedCategories[index].seo && updatedCategories[index].seo.metaDescription) ||
                    "",
                metaKeywords:
                    updatedData.metaKeywords ||
                    (updatedCategories[index].seo && updatedCategories[index].seo.metaKeywords) ||
                    "",
            },
        };
        await set(ref(database, "categories"), updatedCategories);
        fetchCategories();
    };

    // Delete Category (with image deletion)
    const deleteCategory = async (index) => {
        if (!window.confirm("Are you sure you want to delete this category?"))
            return;

        const categoryToDelete = categories[index];
        if (categoryToDelete.categoryUrl) {
            const parts = categoryToDelete.categoryUrl.split("/");
            const filename = parts[parts.length - 1];
            try {
                await axios.delete(`${API_URL}delete`, { data: { filename } });
                console.log("Category image deleted successfully");
            } catch (err) {
                console.error("Error deleting category image", err);
            }
        }
        const updatedCategories = categories.filter((_, i) => i !== index);
        await set(ref(database, "categories"), updatedCategories);
        fetchCategories();
    };

    // Replace Category Image
    const updateCategoryImage = async (index, file) => {
        if (!file) return;
        const existingUrl = categories[index].categoryUrl;
        if (existingUrl) {
            const parts = existingUrl.split("/");
            const filename = parts[parts.length - 1];
            try {
                await axios.delete(`${API_URL}delete`, { data: { filename } });
                console.log("Old image deleted successfully");
            } catch (err) {
                console.error("Error deleting old image", err);
            }
        }
        const newImageUrl = await uploadCategoryImage(file);
        if (newImageUrl) {
            const updatedCategories = [...categories];
            updatedCategories[index].categoryUrl = newImageUrl;
            await set(ref(database, "categories"), updatedCategories);
            fetchCategories();
        }
    };

    const handleReplaceImageChange = async (e) => {
        if (e.target.files && e.target.files[0] && editingImageIndex !== null) {
            await updateCategoryImage(editingImageIndex, e.target.files[0]);
            setEditingImageIndex(null);
            e.target.value = "";
        }
    };

    // Category Image for Add
    const handleAddImageClick = (e) => {
        e.target.nextSibling.click();
    };

    const handleAddImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setNewCategoryImage(e.target.files[0]);
        }
    };

    // Search Handlers (using a generic SearchBar)
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setShowSuggestions(true);
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchTerm(suggestion);
        setShowSuggestions(false);
    };

    const filteredCategories = categories.filter((cat) =>
        cat.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Toggle sidebar on mobile
    const toggleSidebar = () => {
        setShowSidebar((prev) => !prev);
    };

    if (!isAuthenticated) {
        return (
            <div className="admin-container">
                <AdminLoginPage onLoginSuccess={() => setIsAuthenticated(true)} />
            </div>
        );
    }

    return (
        <div className="admin-container">
            <TopBar toggleSidebar={toggleSidebar} handleLogout={handleLogout} />
            <div className="main-section">
                {showSidebar && (
                    <Sidebar
                        activeSection={activeSection}
                        setActiveSection={setActiveSection}
                        toggleSidebar={toggleSidebar}
                    />
                )}
                <div className="content">
                    {activeSection === "categories" && (
                        <>
                            <CategoriesManager
                                categories={searchTerm ? filteredCategories : categories}
                                newCategory={newCategory}
                                newCategoryImage={newCategoryImage}
                                newMetaTitle={newMetaTitle}
                                newMetaDescription={newMetaDescription}
                                newMetaKeywords={newMetaKeywords}
                                searchTerm={searchTerm}
                                showSuggestions={showSuggestions}
                                filteredCategories={filteredCategories}
                                setNewCategory={setNewCategory}
                                setNewCategoryImage={setNewCategoryImage}
                                setNewMetaTitle={setNewMetaTitle}
                                setNewMetaDescription={setNewMetaDescription}
                                setNewMetaKeywords={setNewMetaKeywords}
                                handleSearchChange={handleSearchChange}
                                handleSuggestionClick={handleSuggestionClick}
                                handleAddImageClick={handleAddImageClick}
                                handleAddImageChange={handleAddImageChange}
                                addCategory={addCategory}
                                editCategory={editCategory}
                                deleteCategory={deleteCategory}
                                setEditingImageIndex={setEditingImageIndex}
                                imageEditInputRef={imageEditInputRef}
                                cardConfig={cardConfig}
                            />
                            {/* Hidden file inputs for editing/replacing images */}
                            <input
                                type="file"
                                accept="image/*"
                                ref={editFileInputRef}
                                style={{ display: "none" }}
                            />
                            <input
                                type="file"
                                accept="image/*"
                                ref={imageEditInputRef}
                                style={{ display: "none" }}
                                onChange={handleReplaceImageChange}
                            />
                        </>
                    )}
                    {activeSection === "HeadMetaConfigEditor" && <HeadMetaConfigEditor />}
                    {activeSection === "products" && <ProductsPanel />}
                    {activeSection === "cardConfig" && <CardConfigEditor />}
                    {activeSection === "homeConfig" && <HomeConfigEditor />}
                    {activeSection === "aboutUsConfig" && <AboutUsConfigEditor />}
                    {activeSection === "floatingIconsConfig" && <FloatingIconsConfigEditor />}
                    {activeSection === "navbarConfig" && <NavbarConfigEditor />}
                    {activeSection === "footerConfig" && <FooterConfigEditor />}
                    {activeSection === "ProductDescriptionConfigEditor" && <ProductDescriptionConfigEditor />}

                </div>
            </div>
        </div>
    );
};

export default AdminPanel;
